import { cloneDeep, isEqual, isEqualWith, last, times, uniq } from 'lodash'
import { schema } from 'prose-core'
import { Fragment, Node as ProseNode, ResolvedPos, Slice } from 'prosemirror-model'
import { Selection, SelectionRange, TextSelection } from 'prosemirror-state'
import { inSameTable } from 'prosemirror-tables'
import { Mappable } from 'prosemirror-transform'
import { pointsAtCell } from './pointsAtCell'
import { TableMap } from './TableMap'

export class CellSelection extends Selection {
  $anchorCell: ResolvedPos
  $headCell: ResolvedPos
  $includedCells: ResolvedPos[]
  doc: ProseNode

  get $fromCell(): ResolvedPos {
    return this.$anchorCell.pos <= this.$headCell.pos ? this.$anchorCell : this.$headCell
  }

  get $toCell(): ResolvedPos {
    return this.$anchorCell.pos >= this.$headCell.pos ? this.$anchorCell : this.$headCell
  }

  get isSingleCellSelection(): boolean {
    return this.$anchorCell === this.$headCell
  }

  get isSingleMergedCellSelection(): boolean {
    return (
      this.isSingleCellSelection && // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (this.$anchorCell.nodeAfter!.attrs.colspan > 1 || this.$anchorCell.nodeAfter!.attrs.rowspan > 1)
    )
  }

  /**
   *
   * @param $anchorCell ResolvedPos - Cell that does not move.
   * @param $headCell ResolvedPos - Cell that moves.
   * @param $includedCells ResolvedPos[] - Cells that are part of the selection. Required if $anchorCell and $headCell are not the same cell.
   */
  constructor($anchorCell: ResolvedPos, $headCell: ResolvedPos, $includedCells?: ResolvedPos[]) {
    if ($includedCells === undefined) {
      if ($anchorCell.pos !== $headCell.pos) throw new Error('Must provide $includedCells')
      $includedCells = [$anchorCell]
    }

    let doc = $anchorCell.node(0)

    let ranges = $includedCells.map(($cell) => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      let cellNode = doc.nodeAt($cell.pos)!
      return new SelectionRange(doc.resolve($cell.pos), doc.resolve($cell.pos + cellNode.content.size + 1))
    })

    super($anchorCell, $headCell, ranges)

    this.$anchorCell = $anchorCell
    this.$headCell = $headCell
    this.$includedCells = $includedCells
    this.doc = doc

    this.visible = false
  }

  eq(other: Selection): boolean {
    return (
      other instanceof CellSelection &&
      other.$anchorCell.pos === this.$anchorCell.pos &&
      isEqualWith(this.$includedCells, other.$includedCells, (val, other, i) => {
        if (i !== undefined) return val.pos === other.pos
      })
    )
  }

  content(): Slice {
    const tableNode: ProseNode = this.$anchorCell.node(-1)
    if (tableNode.type.name !== 'table') {
      throw new Error(`Selection content error, could not find tableNode`)
    }
    const cellNodes = this.$includedCells.map((rp) => rp.nodeAfter)
    const includedTableRows = this.$includedCells.map((rp) => rp.parent)

    const newTableNode: ProseNode = cloneDeep(tableNode.copy(tableNode.content))
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const eligibleRows = newTableNode.content
      .content!.map((tableRow, idx) => {
        if (includedTableRows.includes(tableNode.child(idx))) {
          return tableRow
        }
        return undefined
      })
      .map((tableRowNode, tableRowIdx) => {
        if (tableRowNode && tableRowNode.content.content) {
          const eligibleCells = tableRowNode.content.content.filter((_, idx) =>
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            cellNodes.includes(tableNode.content.content![tableRowIdx].content.content![idx]),
          )
          tableRowNode.content = Fragment.from(eligibleCells)
        }
        return tableRowNode
      })
      .filter((tr) => tr !== undefined) as ProseNode[]
    newTableNode.content = Fragment.from(eligibleRows)
    return new Slice(Fragment.from(newTableNode), 0, 0)
  }

  map(doc: ProseNode, mapping: Mappable): Selection {
    let $newAnchorCell = doc.resolve(mapping.map(this.$anchorCell.pos))
    let $newHeadCell = doc.resolve(mapping.map(this.$headCell.pos))
    let newIncludedCells = this.$includedCells.map(($ic) => doc.resolve(mapping.map($ic.pos)))
    if (pointsAtCell($newAnchorCell) && pointsAtCell($newHeadCell) && inSameTable($newAnchorCell, $newHeadCell)) {
      const csi = this.colSelectionIndex()
      const rsi = this.rowSelectionIndex()
      const posBeforeTable = doc.resolve($newAnchorCell.before($newAnchorCell.depth - 1))
      if (csi !== -1) {
        return CellSelection.columnSelection(posBeforeTable, csi)
      } else if (rsi !== -1) {
        return CellSelection.rowSelection(posBeforeTable, rsi)
      } else {
        return new CellSelection($newAnchorCell, $newHeadCell, newIncludedCells)
      }
    }
    return TextSelection.between($newAnchorCell, $newHeadCell)
  }

  /**
   * If selection is a column selection, returns the index of that column. Otherwise, returns -1.
   *
   * @example
   * ```
   * selection.colSelectionIndex() // returns 3 (or -1 if it is not a column selection)
   * ```
   */
  colSelectionIndex() {
    const tableNode: ProseNode = this.$anchorCell.node(-1)
    const posBeforeTable: number = this.$anchorCell.before(this.$anchorCell.depth - 1)
    const tableMap = new TableMap({ posBeforeTable, node: tableNode })
    const cellPosListList = times(tableMap.numCols, (colIdx) => uniq(tableMap.getPosListInColumn(colIdx)))
    const selectedCellPosList = this.$includedCells.map((ic) => ic.pos)

    return cellPosListList.findIndex((cpl) => isEqual(cpl, selectedCellPosList))
  }

  /**
   * If selection is a row selection, returns the index of that row. Otherwise, returns -1.
   *
   * @example
   * ```
   * selection.rowSelectionIndex() // returns 3 (or -1 if it is not a row selection)
   * ```
   */
  rowSelectionIndex() {
    const tableNode: ProseNode = this.$anchorCell.node(-1)
    const posBeforeTable: number = this.$anchorCell.before(this.$anchorCell.depth - 1)
    const tableMap = new TableMap({ posBeforeTable, node: tableNode })
    const cellPosListList = times(tableMap.numRows, (rowIdx) => uniq(tableMap.getOrderedPosListInRow(rowIdx)))
    const selectedCellPosList = this.$includedCells.map((ic) => ic.pos)

    return cellPosListList.findIndex((cpl) => isEqual(cpl, selectedCellPosList))
  }

  toJSON() {
    return {
      type: 'cellSelection',
      anchor: this.$anchorCell.pos,
      includedCells: this.$includedCells.map((ic) => ic.start()),
    }
  }

  static columnSelection($posBeforeTable: ResolvedPos, columnIndex: number) {
    const doc = $posBeforeTable.node(0)
    const table = $posBeforeTable.nodeAfter
    if (table) {
      const tableMap = new TableMap({ posBeforeTable: $posBeforeTable.pos, node: table })
      const positions = uniq(tableMap.getPosListInColumn(columnIndex))
      return new CellSelection(
        doc.resolve(positions[0]),
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        doc.resolve(last(positions)!),
        positions.map((pos) => doc.resolve(pos)),
      )
    } else {
      throw new Error('columnSelection error: could not find table node.')
    }
  }

  static rowSelection($posBeforeTable: ResolvedPos, rowIndex: number) {
    const doc = $posBeforeTable.node(0)
    const table = $posBeforeTable.nodeAfter
    if (table) {
      const tableMap = new TableMap({ posBeforeTable: $posBeforeTable.pos, node: table })
      const positions = uniq(tableMap.getOrderedPosListInRow(rowIndex))
      return new CellSelection(
        doc.resolve(positions[0]),
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        doc.resolve(last(positions)!),
        positions.map((pos) => doc.resolve(pos)),
      )
    } else {
      throw new Error('rowSelection error: could not find table node')
    }
  }

  getBookmark() {
    return new CellBookmark(
      this.$anchorCell.pos,
      this.$headCell.pos,
      this.$includedCells.map((ic) => ic.pos),
    )
  }
}

class CellBookmark {
  anchor: number
  head: number
  includedCells: number[]

  constructor(anchor: number, head: number, includedCells: number[]) {
    this.anchor = anchor
    this.head = head
    this.includedCells = includedCells
  }
  map(mapping: Mappable) {
    return new CellBookmark(
      mapping.map(this.anchor),
      mapping.map(this.head),
      this.includedCells.map((ic) => mapping.map(ic)),
    )
  }
  resolve(doc: ProseNode): Selection {
    let $anchorCell = doc.resolve(this.anchor),
      $headCell = doc.resolve(this.head),
      $includedCells = this.includedCells.map((ic) => doc.resolve(ic))
    if (
      $anchorCell.parent.type === schema.nodes.table_row &&
      $headCell.parent.type === schema.nodes.table_row &&
      $anchorCell.index() < $anchorCell.parent.childCount &&
      $headCell.index() < $headCell.parent.childCount &&
      inSameTable($anchorCell, $headCell)
    )
      return new CellSelection($anchorCell, $headCell, $includedCells)
    else return Selection.near($headCell, 1)
  }
}

try {
  Selection.jsonID('cellSelection', CellSelection)
} catch (e) {}
